import { useEffect, useState } from "react";
import { listQuestions, type QuestionLog } from "../api/unanswered";
import { ortamCoz } from "../lib/ortam";
import Icon from "../components/icons";

function fmtDate(s: string): string {
  try {
    return new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return s;
  }
}

const SAYFA_BOYU_SECENEKLERI = [10, 25, 50, 100];
const SAYFA_BOYU_VARSAYILAN = 25;

/** Soru günlüğü — sorulan HER sorunun ham kaydı.
 *
 *  Yandaki "Cevaplanmamış" sekmesiyle karıştırma: orası dedupe'lı corpus iş
 *  listesi (aynı soru tek satır, times_asked sayacı, "Çözüldü" akışı). Burası
 *  olay günlüğü — her soruş ayrı satır, kim/nereden/hangi IP.
 *
 *  Varsayılan süzgeç "yalnız cevaplananlar" (sekmenin adı bu). "Tümü" kutusu
 *  gerekli: tanıtım sitesinden gelen REDDEDİLMİŞ sorular iş listesine
 *  yazılmıyor (o liste müşteri bazlı), yani onları görebileceğiniz tek yer
 *  burası. Kutu olmasa anonim trafiğin reddedilen soruları hiçbir ekranda
 *  görünmezdi. */
export default function AnsweredQuestions() {
  const [rows, setRows] = useState<QuestionLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [yalnizCevaplanan, setYalnizCevaplanan] = useState(true);
  const [sayfa, setSayfa] = useState(1);
  const [sayfaBoyu, setSayfaBoyu] = useState(SAYFA_BOYU_VARSAYILAN);

  useEffect(() => {
    let iptal = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const d = await listQuestions({
          answered: yalnizCevaplanan ? true : undefined,
          page: sayfa,
          perPage: sayfaBoyu,
        });
        if (iptal) return;
        setRows(d.items);
        setTotal(d.total);
      } catch (e: unknown) {
        if (!iptal) setErr(e instanceof Error ? e.message : "Yükleme hatası");
      } finally {
        if (!iptal) setLoading(false);
      }
    })();
    return () => {
      iptal = true;
    };
  }, [yalnizCevaplanan, sayfa, sayfaBoyu]);

  const sonSayfa = Math.max(1, Math.ceil(total / sayfaBoyu));

  return (
    <>
      <div className="toolbar">
        <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!yalnizCevaplanan}
            onChange={(e) => { setYalnizCevaplanan(!e.target.checked); setSayfa(1); }}
          />
          Reddedilenleri de göster (tüm günlük)
        </label>
        <button className="btn-primary" onClick={() => setSayfa((s) => s)} disabled={loading}>
          Yenile
        </button>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="data-table-card">
        <div className="data-table-toolbar">
          <span className="data-count" style={{ marginInlineStart: 0 }}>
            {total} kayıt
          </span>
        </div>
        {loading ? (
          <div className="empty-state"><div className="spinner-sm" /><p>Yükleniyor…</p></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <Icon name="inbox" size={34} />
            <p>Henüz soru kaydı yok.</p>
          </div>
        ) : (
          // table-wrap: yapışkan başlık + uzun listede kendi içinde kaydırma (ui-admin §4)
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="num">ID</th>
                  <th className="nowrap">Zaman</th>
                  <th>Kaynak</th>
                  <th>Müşteri</th>
                  <th className="nowrap">IP</th>
                  <th>Cihaz / tarayıcı</th>
                  <th>Soru</th>
                  <th className="num">Parça</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="num">{r.id}</td>
                    <td className="nowrap">{fmtDate(r.created_at)}</td>
                    <td>
                      <span
                        className={"badge " + (r.source === "panel" ? "ok" : "neutral")}
                        title={r.source === "panel" ? "Müşteri panelinden soruldu" : "Tanıtım sitesindeki demo — giriş yok"}
                      >
                        {r.source === "panel" ? "Panel" : "Site"}
                      </span>
                    </td>
                    {/* Site sorularında müşteri bilgisi YOKTUR (girişsiz demo);
                        boş hücre "veri kayıp" gibi okunmasın diye tire. */}
                    <td>{r.org_name || "—"}</td>
                    <td className="nowrap mono">{r.ip || "—"}</td>
                    {/* Ham User-Agent okunabilir özete çevrilir; ham metin
                        title'da kalır — ayrıştırıcı yeni bir tarayıcıyı
                        tanımadığında bakılacak tek kaynak odur.
                        IP tek başına yetmez: ortak çıkış (NAT) arkasındaki on
                        kişi tek satır gibi görünür, bot ayırt edilemez. */}
                    <td
                      className="muted"
                      style={{ fontSize: 12 }}
                      title={r.user_agent
                        ? `${ortamCoz(r.user_agent).detay}\n\n${r.user_agent}`
                        : "User-Agent başlığı gönderilmemiş"}
                    >
                      {r.user_agent ? ortamCoz(r.user_agent).ozet : "—"}
                    </td>
                    <td className="clamp-cell">
                      <div className="clamp" title={r.question}>{r.question}</div>
                    </td>
                    <td className="num">{r.chunks_used ?? "—"}</td>
                    <td>
                      {r.answered
                        ? <span className="badge ok">Cevaplandı</span>
                        : <span className="badge warn">Reddedildi</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sayfa boyu seçici sayfa sayısından bağımsız çizilir — gezinmeyle
          birlikte gizlenseydi 100'ü seçip tek sayfaya düşen kullanıcı 10'a
          geri dönemezdi (MailTracking'de aynı gerekçe). */}
      {total > 0 && (
        <div className="toolbar pager">
          <label className="pager-boy">
            Sayfada
            <select
              value={sayfaBoyu}
              onChange={(e) => { setSayfaBoyu(Number(e.target.value)); setSayfa(1); }}
              title="Sayfa başına kaç kayıt gösterilsin"
            >
              {SAYFA_BOYU_SECENEKLERI.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            kayıt
          </label>
          {sonSayfa > 1 ? (
            <div className="pager-nav">
              <button className="btn-primary" disabled={sayfa <= 1} onClick={() => setSayfa(1)}>« İlk</button>
              <button className="btn-primary" disabled={sayfa <= 1} onClick={() => setSayfa((s) => s - 1)}>‹ Önceki</button>
              <span className="muted">Sayfa {sayfa} / {sonSayfa} ({total} kayıt)</span>
              <button className="btn-primary" disabled={sayfa >= sonSayfa} onClick={() => setSayfa((s) => s + 1)}>Sonraki ›</button>
              <button className="btn-primary" disabled={sayfa >= sonSayfa} onClick={() => setSayfa(sonSayfa)}>Son »</button>
            </div>
          ) : (
            <span className="muted">Tek sayfa · {total} kayıt</span>
          )}
        </div>
      )}
    </>
  );
}
